// src/utils/tactic/teamScout.js
// ─────────────────────────────────────────────────────────────────────────
// Scouting numbers for the tactic board: pick a team, then ask of any hero
// "who did it face, how often, and how did that go" — and "what does it get
// played with".
//
// Everything here reads the per-game log (games[] from parseGamesFromExcel),
// never a summary sheet, because the questions are all about the OTHER team's
// heroes in the same game — which only the log records. The row builders are
// shared with the TierData matchup panel (rolePicks / isFsWin) so a win rate
// on the board and a win rate on that page can never drift apart.
// ─────────────────────────────────────────────────────────────────────────

import { buildComboStatsFromGames } from "../comboStatsFromGames";
import { canonicalHeroName } from "../heroCatalog";
import { MATCHUP_ROLES, isFsWin, rolePicks } from "../heroMatchups";

export const SCOUT_ROLES = MATCHUP_ROLES; // DSL JUG MID ADL SUP

/** The workbook's role codes, in coach Thai. */
export const ROLE_LABELS = {
  DSL: "ออฟเลน",
  JUG: "ป่า",
  MID: "มิด",
  ADL: "แครี่",
  SUP: "โรม",
};

/**
 * Row order on the coach's DRAFT 2026 board: CARRY, JUNGLE, MAGE, FIGHTER, then
 * TANK and SUPPORT. The sheet reads in that order rather than the workbook's
 * lane order, so the two can be scanned side by side — and each row carries the
 * board's own word for it, which is what a coach says out loud.
 */
export const BOARD_LANES = [
  { role: "ADL", board: "CARRY" },
  { role: "JUG", board: "JUNGLE" },
  { role: "MID", board: "MAGE" },
  { role: "DSL", board: "FIGHTER" },
  { role: "SUP", board: "TANK / SUPPORT" },
];

/**
 * The board's six rows, keyed to the hero catalog's class names — the coach's
 * sheet IS the catalog grouped this way, which is why the counts line up
 * (Carry 21, Assasin 25, Mage 34, Fighter 30, Tank 19, Support 10).
 *
 * Used for the whole-pool view, where a hero that was never played has no lane
 * to file it under; the played view groups by BOARD_LANES instead.
 */
export const BOARD_CLASS_ROWS = [
  { key: "CARRY", label: "แครี่", board: "CARRY", lane: "ADL", classes: ["Carry", "Marksman"] },
  { key: "JUNGLE", label: "ป่า", board: "JUNGLE", lane: "JUG", classes: ["Assasin", "Assassin"] },
  { key: "MAGE", label: "มิด", board: "MAGE", lane: "MID", classes: ["Mage"] },
  { key: "FIGHTER", label: "ออฟเลน", board: "FIGHTER", lane: "DSL", classes: ["Fighter", "Warrior"] },
  { key: "TANK", label: "แทงค์", board: "TANK", lane: "SUP", classes: ["Tank"] },
  { key: "SUPPORT", label: "ซัพพอร์ต", board: "SUPPORT", lane: "SUP", classes: ["Support"] },
];

// The catalog splits a two-shaped hero into "Flowborn Carry" / "Flowborn Mage";
// the scrim sheet writes plain "Flowborn" for both. Strip the class word so the
// form can find its own numbers.
const CLASS_SUFFIX = /\s+(carry|marksman|mage|assassin|assasin|fighter|warrior|tank|support)$/i;

function baseFormName(name) {
  const raw = String(name || "").trim();
  const base = raw.replace(CLASS_SUFFIX, "").trim();
  return base && base !== raw ? base : "";
}

/** Catalog class → the lane it stands in, for splitting a two-formed hero. */
export const LANE_OF_CLASS = {
  Carry: "ADL",
  Marksman: "ADL",
  Assasin: "JUG",
  Assassin: "JUG",
  Mage: "MID",
  Fighter: "DSL",
  Warrior: "DSL",
  Tank: "SUP",
  Support: "SUP",
};

const NO_RECORD = { role: "", roles: {}, games: 0, wins: 0, losses: 0, winRate: 0 };

/**
 * A catalog name's record, looked up in rows keyed by the log's names.
 *
 * Most heroes match outright. A hero the catalog splits into forms — "Flowborn
 * Carry" / "Flowborn Mage" against the sheet's plain "Flowborn" — resolves to
 * its base name and takes only the games played in `lane`, and carries
 * `logName` so a click can still find them.
 *
 * @returns a row, or null when neither the name nor its base has been played
 */
export function statsForCatalogName(byName, name, lane = "") {
  const canon = canonicalHeroName(name);
  if (!canon) return null;

  const exact = byName?.get(canon);
  if (exact) return exact;

  const base = baseFormName(canon);
  const parent = base ? byName?.get(canonicalHeroName(base)) : null;
  if (!parent) return null;

  const slice = lane ? parent.byRole?.[lane] : null;
  if (!slice?.games) {
    return { ...NO_RECORD, name: canon, logName: parent.name, unplayed: true };
  }
  return {
    name: canon,
    logName: parent.name,
    role: lane,
    roles: { [lane]: slice.games },
    byRole: { [lane]: slice },
    games: slice.games,
    wins: slice.wins,
    losses: slice.games - slice.wins,
    winRate: pct(slice.wins, slice.games),
  };
}

const ALL_TEAMS = "__all__";
export const ALL_TEAMS_KEY = ALL_TEAMS;

const pct = (wins, games) => (games ? Number(((wins / games) * 100).toFixed(1)) : 0);

const teamCode = (game) => String(game?.competitor || "").trim();

/** A win for the side we are looking at — "op" flips the log's FS result. */
const wonBy = (game, side) => (side === "op" ? !isFsWin(game) : isFsWin(game));

const asGames = (games) => (Array.isArray(games) ? games : []);

/** Every opponent the log has seen, most-played first. */
export function teamsInLog(games) {
  const acc = new Map();
  for (const game of asGames(games)) {
    const code = teamCode(game);
    if (!code) continue;
    const slot = acc.get(code) || { games: 0, wins: 0 };
    slot.games += 1;
    if (isFsWin(game)) slot.wins += 1;
    acc.set(code, slot);
  }
  return [...acc.entries()]
    .map(([code, v]) => ({
      code,
      games: v.games,
      wins: v.wins,
      losses: v.games - v.wins,
      winRate: pct(v.wins, v.games),
    }))
    .sort((a, b) => b.games - a.games || a.code.localeCompare(b.code));
}

/** `ALL_TEAMS_KEY` (or an empty code) keeps the whole log. */
export function gamesVsTeam(games, code) {
  const want = String(code || "").trim();
  if (!want || want === ALL_TEAMS) return asGames(games);
  return asGames(games).filter((g) => teamCode(g).toUpperCase() === want.toUpperCase());
}

/** Our record over the games on screen — one opponent, or the whole log. */
export function recordVsTeam(games, code) {
  const pool = gamesVsTeam(games, code);
  let wins = 0;
  for (const game of pool) if (isFsWin(game)) wins += 1;
  return {
    code: String(code || ""),
    games: pool.length,
    wins,
    losses: pool.length - wins,
    winRate: pct(wins, pool.length),
  };
}

/**
 * Scouted rows (Specter_Scrim) as games seen from one team's bench.
 *
 * The scouting sheet holds other teams' scrims — no FS in it — so a row is
 * turned into the shape the rest of this file reads, with the scouted team on
 * the "op" side and whoever they were playing on the "fs" side. `win` is the
 * FS-side flag the log uses, so it is 1 when the OTHER team won.
 *
 * `ALL_TEAMS_KEY` yields both benches of every row, so every scouted hero shows
 * up with its own record.
 *
 * @param rows from parseSpecterScrimFromWorkbook
 */
export function specterGamesForTeam(rows, code) {
  const want = String(code || "").trim();
  const all = !want || want === ALL_TEAMS;
  const out = [];

  for (const row of Array.isArray(rows) ? rows : []) {
    const sides = [
      { team: row.teamA, byRole: row.aByRole, vs: row.teamB, vsByRole: row.bByRole },
      { team: row.teamB, byRole: row.bByRole, vs: row.teamA, vsByRole: row.aByRole },
    ];

    for (const side of sides) {
      const team = String(side.team || "").trim();
      if (!team) continue;
      if (!all && team.toUpperCase() !== want.toUpperCase()) continue;

      out.push({
        Date: row.Date,
        dateStr: row.dateStr,
        week: row.week,
        type: row.type,
        competitor: team,
        // The scouted team reads as the opponent; their opponent takes our seat.
        opByRole: side.byRole,
        fsByRole: side.vsByRole,
        win: String(row.winner || "").trim().toUpperCase() === team.toUpperCase() ? 0 : 1,
        scouted: true,
        versus: String(side.vs || "").trim(),
      });
    }
  }

  return out;
}

/**
 * The scouted games as an "opponent pool" — the shape the draft page already
 * reads for a team (oneHero + combo2 + combo3), so watched games can sit beside
 * the summary sheets instead of needing their own reader.
 */
export function scoutedOpponentPool(specterRows, code) {
  const games = specterGamesForTeam(specterRows, code);
  const oneHero = [];

  for (const row of heroesOnSide(games, "op")) {
    for (const lane of SCOUT_ROLES) {
      const rec = row.byRole?.[lane];
      if (!rec?.games) continue;
      oneHero.push({
        hero: row.name,
        position: lane,
        total: rec.games,
        win: rec.wins,
        winrate: pct(rec.wins, rec.games),
      });
    }
  }

  const { combo2, combo3 } = buildComboStatsFromGames(games, "op");
  return {
    games: games.length,
    oneHero: oneHero.sort((a, b) => b.total - a.total || a.hero.localeCompare(b.hero)),
    combo2,
    combo3,
  };
}

/** Add two opponent pools, summing any row the two sheets both carry. */
export function mergeOppPools(a, b) {
  const add = (rows, keyOf) => {
    const acc = new Map();
    for (const row of rows) {
      const key = keyOf(row);
      const found = acc.get(key);
      if (!found) {
        acc.set(key, { ...row });
        continue;
      }
      found.total = (found.total || 0) + (row.total || 0);
      found.win = (found.win || 0) + (row.win || 0);
      if ("losses" in found) found.losses = Math.max(0, found.total - found.win);
      if ("winrate" in found) found.winrate = pct(found.win, found.total);
    }
    return [...acc.values()].sort((x, y) => (y.total || 0) - (x.total || 0));
  };

  const rows = (pool, key) => (Array.isArray(pool?.[key]) ? pool[key] : []);
  return {
    oneHero: add([...rows(a, "oneHero"), ...rows(b, "oneHero")], (r) => `${r.hero}|${r.position}`),
    combo2: add([...rows(a, "combo2"), ...rows(b, "combo2")], (r) => `${r.combo}|${r.comboType}`),
    combo3: add([...rows(a, "combo3"), ...rows(b, "combo3")], (r) => `${r.combo}|${r.comboType}`),
  };
}

/** Teams the scouting sheet has seen, most-watched first. */
export function specterTeams(rows) {
  const acc = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    for (const team of [row.teamA, row.teamB]) {
      const code = String(team || "").trim();
      if (!code) continue;
      acc.set(code, (acc.get(code) || 0) + 1);
    }
  }
  return [...acc.entries()]
    .map(([code, games]) => ({ code, games }))
    .sort((a, b) => b.games - a.games || a.code.localeCompare(b.code));
}

/**
 * The team picker's list: everyone we have played, plus teams only the scouting
 * sheet knows — a team we have never met is exactly the one worth scouting.
 */
export function teamsForPicker(games, specterRows) {
  const played = teamsInLog(games);
  const byCode = new Map(played.map((t) => [t.code.toUpperCase(), { ...t, scouted: 0 }]));

  for (const { code, games: n } of specterTeams(specterRows)) {
    const key = code.toUpperCase();
    const found = byCode.get(key);
    if (found) found.scouted = n;
    else byCode.set(key, { code, games: 0, wins: 0, losses: 0, winRate: 0, scouted: n });
  }

  // Most to read first; on a tie our own games outrank watched ones, since a
  // team we have actually faced is the more useful row.
  return [...byCode.values()].sort(
    (a, b) =>
      b.games + b.scouted - (a.games + a.scouted) ||
      b.games - a.games ||
      a.code.localeCompare(b.code)
  );
}

/** The lane a hero appeared in most often, blank when it never appeared. */
function topRole(counts) {
  let best = "";
  for (const role of SCOUT_ROLES) {
    if ((counts[role] || 0) > (counts[best] || 0)) best = role;
  }
  return best;
}

/**
 * Heroes one side actually played in these games, most-played first.
 *
 * @param side "fs" = ours, "op" = the opponent's
 */
export function heroesOnSide(games, side = "fs") {
  const acc = new Map();

  for (const game of asGames(games)) {
    const mine = rolePicks(game, side);
    const won = wonBy(game, side);

    for (const role of SCOUT_ROLES) {
      const hero = mine[role];
      if (!hero) continue;
      const slot = acc.get(hero) || { games: 0, wins: 0, roles: {}, byRole: {} };
      slot.games += 1;
      if (won) slot.wins += 1;
      slot.roles[role] = (slot.roles[role] || 0) + 1;
      const lane = slot.byRole[role] || { games: 0, wins: 0 };
      lane.games += 1;
      if (won) lane.wins += 1;
      slot.byRole[role] = lane;
      acc.set(hero, slot);
    }
  }

  return [...acc.entries()]
    .map(([name, v]) => ({
      name,
      role: topRole(v.roles),
      roles: v.roles,
      // Per-lane records: a hero the sheet writes one name for but plays in two
      // shapes (Flowborn ADL / MID) is split by these.
      byRole: v.byRole,
      games: v.games,
      wins: v.wins,
      losses: v.games - v.wins,
      winRate: pct(v.wins, v.games),
    }))
    .sort((a, b) => b.games - a.games || b.winRate - a.winRate || a.name.localeCompare(b.name));
}

/**
 * "This hero met who, how often, and how did it go."
 *
 * @param hero      the hero on `side`
 * @param side      "fs" | "op" — which team the hero belongs to
 * @param heroRole  only count games where the hero stood in this lane ("" = any)
 * @param enemyRole only count opposing heroes standing in this lane ("" = all five)
 */
export function matchupsForHero(games, { hero, side = "fs", heroRole = "", enemyRole = "" } = {}) {
  const want = String(hero || "").trim();
  const empty = { totalGames: 0, wins: 0, losses: 0, winRate: 0, rows: [] };
  if (!want) return empty;

  const theirSide = side === "op" ? "fs" : "op";
  const lanes = enemyRole ? [enemyRole] : SCOUT_ROLES;
  const acc = new Map();
  let totalGames = 0;
  let wins = 0;

  for (const game of asGames(games)) {
    const mine = rolePicks(game, side);
    const myLane = heroRole
      ? (mine[heroRole] === want ? heroRole : "")
      : SCOUT_ROLES.find((r) => mine[r] === want) || "";
    if (!myLane) continue;

    const theirs = rolePicks(game, theirSide);
    const won = wonBy(game, side);
    totalGames += 1;
    if (won) wins += 1;

    // One row per enemy hero per game, even for a hero that somehow shows up
    // in two lanes of the same row (a mis-keyed sheet does happen).
    const seen = new Set();
    for (const role of lanes) {
      const enemy = theirs[role];
      if (!enemy || seen.has(enemy)) continue;
      seen.add(enemy);
      const slot = acc.get(enemy) || { games: 0, wins: 0, roles: new Set() };
      slot.games += 1;
      if (won) slot.wins += 1;
      slot.roles.add(role);
      acc.set(enemy, slot);
    }
  }

  const rows = [...acc.entries()]
    .map(([name, v]) => ({
      name,
      // Blank when the same hero was met in more than one lane — the portrait
      // picker uses this and a wrong lane picks the wrong Flowborn art.
      role: v.roles.size === 1 ? [...v.roles][0] : "",
      games: v.games,
      wins: v.wins,
      losses: v.games - v.wins,
      winRate: pct(v.wins, v.games),
    }))
    .sort((a, b) => b.games - a.games || b.winRate - a.winRate || a.name.localeCompare(b.name));

  return { totalGames, wins, losses: totalGames - wins, winRate: pct(wins, totalGames), rows };
}

/**
 * The whole hero pool in the board's rows, with this side's numbers attached.
 *
 * A hero the log has never seen still gets a tile — that is the point of the
 * view — carrying zero games and `unplayed`, so the tile can say so instead of
 * showing a 0% that reads like a losing record. Dual-class heroes appear in
 * both of their rows, exactly as they do on the board.
 *
 * @param catalog heroList.json rows: { name, role }
 * @param played  rows from heroesOnSide for the side being shown
 */
export function heroPoolRows(catalog, played) {
  const stats = new Map((Array.isArray(played) ? played : []).map((r) => [r.name, r]));
  const rows = Array.isArray(catalog) ? catalog : [];

  return BOARD_CLASS_ROWS.map(({ key, label, board, lane, classes }) => {
    const wanted = new Set(classes);
    const seen = new Set();
    const out = [];

    for (const entry of rows) {
      if (!entry?.name || !wanted.has(entry.role)) continue;
      const name = canonicalHeroName(entry.name);
      if (!name || seen.has(name)) continue;
      seen.add(name);

      out.push(
        statsForCatalogName(stats, name, lane) || { ...NO_RECORD, name, unplayed: true }
      );
    }

    // What the team actually plays leads its row; the rest follows by name.
    out.sort((a, b) => b.games - a.games || a.name.localeCompare(b.name));
    return { key, label, board, rows: out };
  });
}

/**
 * "Who does this hero play well WITH" — the other four on its own side.
 *
 * Same row shape as matchupsForHero so the two can be ranked and drawn by the
 * same code; the difference is which bench it reads.
 */
export function teammatesForHero(games, { hero, side = "fs", heroRole = "" } = {}) {
  const want = String(hero || "").trim();
  const empty = { totalGames: 0, wins: 0, losses: 0, winRate: 0, rows: [] };
  if (!want) return empty;

  const acc = new Map();
  let totalGames = 0;
  let wins = 0;

  for (const game of asGames(games)) {
    const mine = rolePicks(game, side);
    const myLane = heroRole
      ? (mine[heroRole] === want ? heroRole : "")
      : SCOUT_ROLES.find((r) => mine[r] === want) || "";
    if (!myLane) continue;

    const won = wonBy(game, side);
    totalGames += 1;
    if (won) wins += 1;

    for (const role of SCOUT_ROLES) {
      if (role === myLane) continue;
      const mate = mine[role];
      if (!mate || mate === want) continue;
      const slot = acc.get(mate) || { games: 0, wins: 0, roles: new Set() };
      slot.games += 1;
      if (won) slot.wins += 1;
      slot.roles.add(role);
      acc.set(mate, slot);
    }
  }

  const rows = [...acc.entries()]
    .map(([name, v]) => ({
      name,
      role: v.roles.size === 1 ? [...v.roles][0] : "",
      games: v.games,
      wins: v.wins,
      losses: v.games - v.wins,
      winRate: pct(v.wins, v.games),
    }))
    .sort((a, b) => b.games - a.games || b.winRate - a.winRate || a.name.localeCompare(b.name));

  return { totalGames, wins, losses: totalGames - wins, winRate: pct(wins, totalGames), rows };
}

/** Every k-sized subset of `list`, order preserved. */
function combinations(list, k) {
  if (k <= 0 || k > list.length) return [];
  if (k === 1) return list.map((x) => [x]);
  const out = [];
  list.forEach((item, i) => {
    for (const rest of combinations(list.slice(i + 1), k - 1)) out.push([item, ...rest]);
  });
  return out;
}

/**
 * "What does this hero get played with." Comps are counted from the log, so a
 * 2-comp is the hero plus one lane-mate and a 3-comp is the hero plus two —
 * every pairing the team actually fielded, not the eight fixed role blocks the
 * Combo_Stats pivot happens to cover.
 *
 * @param size 2 or 3 — heroes in the comp, INCLUDING this one
 */
export function compsForHero(games, { hero, side = "fs", size = 2, heroRole = "" } = {}) {
  const want = String(hero || "").trim();
  if (!want || size < 2 || size > 5) return [];

  const acc = new Map();

  for (const game of asGames(games)) {
    const mine = rolePicks(game, side);
    const myLane = heroRole
      ? (mine[heroRole] === want ? heroRole : "")
      : SCOUT_ROLES.find((r) => mine[r] === want) || "";
    if (!myLane) continue;

    const mates = SCOUT_ROLES.filter((r) => r !== myLane && mine[r] && mine[r] !== want);
    const won = wonBy(game, side);

    for (const picked of combinations(mates, size - 1)) {
      // Label in lane order, so the same comp keys the same way every game.
      const roles = [myLane, ...picked].sort(
        (a, b) => SCOUT_ROLES.indexOf(a) - SCOUT_ROLES.indexOf(b)
      );
      const heroes = roles.map((r) => mine[r]);
      const key = heroes.join(" + ");
      const slot = acc.get(key) || { games: 0, wins: 0, heroes, roles };
      slot.games += 1;
      if (won) slot.wins += 1;
      acc.set(key, slot);
    }
  }

  return [...acc.entries()]
    .map(([name, v]) => ({
      name,
      heroes: v.heroes,
      roles: v.roles,
      games: v.games,
      wins: v.wins,
      losses: v.games - v.wins,
      winRate: pct(v.wins, v.games),
    }))
    .sort((a, b) => b.games - a.games || b.winRate - a.winRate || a.name.localeCompare(b.name));
}

/**
 * Top/bottom N by win rate.
 *
 * `minGames` is the whole point of the split: a 100% row off one game is not a
 * best matchup, it is one game. Rows below the cut are simply left out of both
 * lists rather than flagged.
 */
export function rankByWinRate(rows, { minGames = 2, count = 5 } = {}) {
  const pool = (Array.isArray(rows) ? rows : []).filter((r) => r.games >= minGames);
  const byRate = [...pool].sort(
    (a, b) => b.winRate - a.winRate || b.games - a.games || a.name.localeCompare(b.name)
  );
  const worst = [...pool].sort(
    (a, b) => a.winRate - b.winRate || b.games - a.games || a.name.localeCompare(b.name)
  );
  return {
    best: byRate.slice(0, count),
    worst: worst.slice(0, count),
    considered: pool.length,
  };
}
